import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { ReferralStatus, RewardStatus, type ReferralUIData } from "@/lib/schema";
import { formatDateForSouthAfrica } from "@/lib/textUtils";
import { COMMON_CLASSES } from "@/lib/jobConstants";
import { JobSkills } from "@/components/jobs/JobSkills";
import { JobDescription } from "@/components/jobs/JobDescription";
import { formatDisplayText, formatRewardAmount } from "@/lib/textUtils";
import { ReferralStatusBadge } from "@/components/referrals/ReferralStatusBadge";
import { RewardStatusBadge } from "@/components/rewards/RewardStatusBadge";
import { ReferralProgressTimeline } from "@/components/referrals/ReferralProgressTimeline";
import { useLocation } from "wouter";
import { 
  ArrowLeft,
  Gift,
  Clock,
  UserCheck,
  Trophy
} from "lucide-react";
import industryIcon from "@/assets/jobs/icon_industry.svg";
import locationIcon from "@/assets/jobs/icon_location.svg";
import calendarIcon from "@/assets/jobs/icon_calendar.svg";

interface ReferralDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  referral: ReferralUIData;
}

// Function to determine reward status based on referral status
const getRewardStatusFromReferralStatus = (referralStatus: ReferralStatus, originalRewardStatus?: string): RewardStatus => {
  switch (referralStatus) {
    case ReferralStatus.PASSED_PROBATION:
      // Only when passed probation can the reward be eligible/paid
      return originalRewardStatus === RewardStatus.PAID ? RewardStatus.PAID : RewardStatus.ELIGIBLE;
    case ReferralStatus.HIRED:
      // When hired, reward is pending probation completion
      return RewardStatus.PENDING;
    case ReferralStatus.REJECTED:
      // When rejected, reward is forfeited
      return RewardStatus.FORFEITED;
    default:
      // For all other statuses (pending, submitted, under_review, shortlisted, interviewed), no reward yet
      return RewardStatus.PENDING;
  }
};

// Function to render status-specific components
const renderStatusComponent = (status: ReferralStatus, onNavigateToRewards: () => void, rejectionNotes?: string) => {
  switch (status) {
    case ReferralStatus.REJECTED:
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg space-grotesk font-semibold text-red-900 mb-2">Referral Not Selected</h3>
              <p className="text-red-800 mb-4">
                Unfortunately, this candidate wasn't selected for this specific role. However, don't worry - your referral is still valuable!
              </p>
              
              {/* Rejection Feedback */}
              {rejectionNotes && (
                <div className="mt-4 p-4 bg-white border border-red-200 rounded-lg mb-4">
                  <h4 className="text-sm font-semibold text-gray-900 space-grotesk mb-2">
                    Feedback
                  </h4>
                  <p className="text-sm text-gray-700 whitespace-pre-line">
                    {rejectionNotes}
                  </p>
                </div>
              )}
              
              <div className="bg-white border border-red-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Future Opportunities</h4>
                    <p className="text-sm text-gray-700">
                      If your referred candidate matches another job role within <strong>6 months</strong>, 
                      a new referral will be automatically created and you'll still earn rewards if they get hired!
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );

    case ReferralStatus.HIRED:
      return (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                <Clock className="w-6 h-6 text-orange-600" />
              </div>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-lg space-grotesk font-semibold text-orange-900">Candidate Hired!</h3>
                <UserCheck className="w-5 h-5 text-orange-600" />
              </div>
              <p className="text-orange-800 mb-4">
                Great news! Your referred candidate has been hired. Your reward is currently pending while they complete their probation period.
              </p>
              <div className="bg-white border border-orange-200 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      <Clock className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Reward Pending</h4>
                      <p className="text-sm text-gray-700">
                        Once the candidate successfully passes their probation period, your reward will be ready for redemption.
                      </p>
                    </div>
                  </div>
                  <Button 
                    onClick={onNavigateToRewards}
                    variant="secondary"
                    size="sm"
                    className="ml-4 flex-shrink-0"
                  >
                    View Rewards
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );

    case ReferralStatus.PASSED_PROBATION:
      return (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <Gift className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-lg space-grotesk font-semibold text-green-900">Reward Ready!</h3>
                <Trophy className="w-5 h-5 text-green-600" />
              </div>
              <p className="text-green-800 mb-4">
                Congratulations! Your referred candidate has successfully passed their probation period. Your reward is now ready to be redeemed!
              </p>
              <div className="bg-white border border-green-200 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      <Gift className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Ready for Redemption</h4>
                      <p className="text-sm text-gray-700">
                        Your reward is available in your rewards section. Click below to view and redeem your earnings!
                      </p>
                    </div>
                  </div>
                  <Button 
                    onClick={onNavigateToRewards}
                    variant="primary"
                    size="sm"
                    className="ml-4 flex-shrink-0"
                  >
                    <Gift className="w-4 h-4 mr-2" />
                    Claim Reward
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );

    default:
      return <ReferralProgressTimeline status={status} />;
  }
};

export function ReferralDetailModal({ isOpen, onClose, referral }: ReferralDetailModalProps) {
  // Note: referral.status already has PENDING mapped to SUBMITTED from the API layer
  const [, setLocation] = useLocation();

  const handleNavigateToRewards = () => {
    onClose(); // Close the modal first
    // Navigate to rewards page using Wouter
    setLocation('/rewards');
  };

  // Calculate the correct reward status based on referral status
  const displayRewardStatus = getRewardStatusFromReferralStatus(referral.status, referral.rewardStatus);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="max-w-[95vw] sm:max-w-2xl lg:max-w-3xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto p-0 border-0 shadow-xl [&::-webkit-scrollbar]:hidden"
        style={{
          scrollbarWidth: 'none', /* Firefox */
          msOverflowStyle: 'none', /* Internet Explorer 10+ */
        }}
      >
        <DialogHeader className="p-3 sm:p-6 lg:p-8 pb-3 sm:pb-6" style={{ paddingTop: 'calc(0.75rem + env(safe-area-inset-top, 0px))' }}>
          <DialogTitle className="sr-only">Referral Details for {referral.candidateName}</DialogTitle>
          <DialogDescription className="sr-only">
            View detailed information about the referral for {referral.candidateName} including progress status, job details, and reward information.
          </DialogDescription>
          
          <div className="flex items-center gap-3 mb-4 sm:mb-6 lg:mb-8">
            <Button variant="ghost" size="icon" onClick={onClose}>
              <ArrowLeft className="text-orange-600 h-5 w-5" />
            </Button>
            <span className="space-grotesk font-semibold text-sm leading-[130%]" style={{ color: '#2D2D2D' }}>Back to referrals</span>
          </div>
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex-1">
              <h2 className="space-grotesk font-semibold leading-[130%] mb-2" style={{ color: '#2D2D2D', fontSize: '28px' }}>{referral.candidateName}</h2>
            </div>
            
            {/* Status and Bonus Badges */}
            <div className="flex flex-col gap-2 items-end">
              {/* Bonus Points Badge - matching ReferralForm.tsx styling */}
              {referral.bonusPoints && (
                <Badge className="bg-gradient-to-r from-orange-400 to-orange-600 text-white text-xs font-bold px-3 py-1 rounded-full space-grotesk uppercase tracking-wide border-0">
                  Bonus
                </Badge>
              )}
              
              {/* Referral Status Badge */}
              <ReferralStatusBadge status={referral.status} />
            </div>
          </div>
        </DialogHeader>

        <div className="px-3 sm:px-6 lg:px-8 pb-6 sm:pb-8 space-y-6 sm:space-y-8 lg:space-y-10">
          {/* Status-specific component (Progress Timeline, Rejected Warning, Hired/Probation Cards) */}
          {renderStatusComponent(referral.status, handleNavigateToRewards, referral.rejectionNotes)}

          {/* Information Sections */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            {/* Referral Information */}
            <div 
              className="p-6 border border-r-0 lg:border-r rounded-l"
              style={{ 
                borderColor: '#EAEAEA',
                background: '#FBFBFB'
              }}
            >
              <h3 className="text-lg font-semibold text-gray-900 space-grotesk mb-4">Referral information</h3>
              <div className="space-y-2">
                <div className="flex items-center">
                  <p 
                    className="text-sm font-semibold leading-[180%] max-w-[100px]"
                    style={{ 
                      color: '#6D6D6D',
                      letterSpacing: '0.28px'
                    }}
                  >
                    Referred on
                  </p>
                  <p 
                    className="font-medium leading-[180%] ml-8 text-sm"
                    style={{ 
                      color: '#2D2D2D',
                      letterSpacing: '0.28px'
                    }}
                  >
                    {formatDateForSouthAfrica(referral.referralDate)}
                  </p>
                </div>
                

                {referral.referralNote && (
                  <div>
                    <p 
                      className="text-sm font-semibold leading-[180%] mb-2"
                      style={{ 
                        color: '#6D6D6D',
                        letterSpacing: '0.28px'
                      }}
                    >
                      Referrer's Note
                    </p>
                    <div className="text-gray-900 space-grotesk text-sm leading-relaxed break-words whitespace-pre-wrap">
                      {referral.referralNote}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Reward Information */}
            <div 
              className="p-6 border border-l-0 lg:border-l-0 rounded-r"
              style={{ 
                borderColor: '#EAEAEA',
                background: '#FFF'
              }}
            >
              <h3 className="text-lg font-semibold text-gray-900 space-grotesk mb-4">Reward information</h3>
              <div className="space-y-2">
                <div className="flex items-start justify-between">
                  <p 
                    className="text-sm font-semibold leading-[180%] min-w-[80px]"
                    style={{ 
                      color: '#6D6D6D',
                      letterSpacing: '0.28px'
                    }}
                  >
                    Amount
                  </p>
                  <p 
                    className="font-bold leading-[140%] text-sm"
                    style={{ 
                      color: '#2D2D2D',
                      letterSpacing: '0.28px'
                    }}
                  >
                    R{formatRewardAmount(referral.reward)}
                  </p>
                </div>
                
                <div className="flex items-start justify-between">
                  <p 
                    className="text-sm font-semibold leading-[180%] min-w-[80px]"
                    style={{ 
                      color: '#6D6D6D',
                      letterSpacing: '0.28px'
                    }}
                  >
                    Reward Status
                  </p>
                  <RewardStatusBadge status={displayRewardStatus} />
                </div>
              </div>
            </div>
          </div>

          {/* Job Details */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Job details</h3>
            
            <Card className={COMMON_CLASSES.card}>
              <CardContent className="p-4 sm:p-6">
                {/* Job Header */}
                <div className="flex flex-col sm:flex-row items-start gap-4 mb-6">
                  <div className="flex-1 w-full">
                    <h2 className={`${COMMON_CLASSES.text.heading} mb-2`}>{referral.jobTitle}</h2>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-gray-600 mb-4">
                      {referral.jobLocation && (
                        <div className="flex items-center gap-1">
                          <img src={locationIcon} alt="Location" className="h-4 w-4" />
                          <span>
                            {referral.jobLocation}
                            {referral.jobRemoteType && ` (${formatDisplayText(referral.jobRemoteType)})`}
                          </span>
                        </div>
                      )}
                      {referral.jobCategory && (
                        <div className="flex items-center gap-1">
                          <img src={industryIcon} alt="Department" className="h-4 w-4" />
                          <span>{formatDisplayText(referral.jobCategory)}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                      {referral.jobEmploymentType && (
                        <Badge variant="outline" className="border-0 bg-gray-100 text-gray-700 w-fit">
                          {formatDisplayText(referral.jobEmploymentType)}
                        </Badge>
                      )}
                      <div className="flex items-center gap-1 text-sm text-gray-500">
                        <img src={calendarIcon} alt="Calendar" className="h-3 w-3" />
                        <span>
                          Referred on {formatDateForSouthAfrica(referral.referralDate)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Job Description */}
                {referral.jobDescription && (
                  <JobDescription 
                    description={referral.jobDescription}
                    containerClassName="space-y-4 pb-6"
                  />
                )}

                {/* Skills */}
                <JobSkills skills={referral.jobSkills || []} />
              </CardContent>
            </Card>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}